import { Component, inject } from '@angular/core';
import { Router } from '@angular/router';
import { HeroComponent } from '../../components/hero/hero.component';
import { LayoutContainerComponent } from '../../components/layout-container/layout-container.component';
import { SectionHeadingComponent } from '../../components/section-heading/section-heading.component';

interface QaQuestion {
  id: string;
  question: string;
  answer: string;
  answerHtml?: string;
  imageSrc?: string;
  imageAlt?: string;
  siteLinkUrl?: string;
  siteLinkLabel?: string;
}

interface QaCategory {
  id: string;
  title: string;
  questions: QaQuestion[];
}

@Component({
  selector: 'app-q-and-a',
  standalone: true,
  imports: [HeroComponent, LayoutContainerComponent, SectionHeadingComponent],
  templateUrl: './q-and-a.component.html',
  styleUrl: './q-and-a.component.css'
})
export class QAndAComponent {
  private readonly router = inject(Router);

  readonly categoryButtons = [
    { id: 'gettingstarted', label: 'GETTING STARTED' },
    { id: 'cost', label: 'COST' },
    { id: 'events', label: 'EVENTS' },
    { id: 'time', label: 'TIME' },
    { id: 'options', label: 'OPTIONS' },
    { id: 'configuration', label: 'CONFIGURATION' },
    { id: 'support', label: 'SUPPORT' },
    { id: 'metrics', label: 'METRICS' },
    { id: 'security', label: 'SECURITY' }  
  ];

  readonly questionCategories: QaCategory[] = [
    {
      id: 'gettingstarted',
      title: 'GETTING STARTED',
      questions: [
        {
          id: 'gettingstarted-1',
          question: 'How do I get started?',
          answer: 'The following steps will help outline the process for getting started with our team building activities.',
          siteLinkUrl: '/services#how-to-get-started',
          siteLinkLabel: 'More Info'
          // imageSrc: 'assets/UTB-Logo.svg',
          // imageAlt: 'United Team Building game overview'
        },
        {
          id: 'gettingstarted-2',
          question: 'How do I reserve a date?',
          answer: 'You can reserve a date by going to the Booking section of Contact Us. Select an available date and complete the Booking information. If the date is available, a Registration email will be sent to allow you to make a payment to reserve the date.',
          answerHtml: 'You can reserve a date by going to the Booking section of <a href="/contact-us#bookings">Contact Us</a>. Select an available date and complete the Booking information. If the date is available, a Registration Email will be sent to allow you to make a payment to reserve the date.'
        },
        {
          id: 'gettingstarted-3',
          question: 'What is registration for?',
          answer: 'Registration allows you to secure a date for your team building event and gives access to the portal for creating events, along with questions and answers for the event.',
        },
        {
          id: 'gettingstarted-4',
          question: 'How do I cancel a reservation?',
          answer: 'You can cancel a reservation by going to the Booking section of Contact Us. Select your reservation and follow the cancellation instructions. If the cancellation is successful, a confirmation email will be sent.',
        }
      ]
    },
    {
      id: 'events',
      title: 'EVENTS',
      questions: [
        {
          id: 'events-1',
          question: 'How does the event work?',
          answer: 'The event is usually configured by an Event Planner. They choose the questions, answers, add users, effects and game modes. Features such as user specific audio and video encorporated into the game, or themes are just some of the options!'
          // imageSrc: 'assets/UTB-Logo.svg',
          // imageAlt: 'United Team Building event overview'
        },
        {
          id: 'events-2',
          question: 'Can events be customized for our team goals?',
          answer: 'Yes. Activities can be tailored to your culture, team size, and outcomes such as onboarding, trust building, or leadership development.'
        },
        {
          id: 'events-3',
          question: 'Can I have multiple sessions?',
          answer: 'Yes. Multiple session packages are available. The same questions can be used again in each new session, or completely different sets of questions can be used.'
        }
      ]
    },
    {
      id: 'cost',
      title: 'COST',
      questions: [
        {
          id: 'cost-1',
          question: 'How is pricing determined for an event?',
          answer: 'Pricing is based on event type, duration, attendee count, and any customization or support add-ons requested.'
        },
        {
          id: 'cost-2',
          question: 'Do you offer package tiers for different budgets?',
          answer: 'Yes. We provide entry, standard, and premium options so organizations can choose the experience that best fits their budget.',
          imageSrc: 'assets/UTB-Logo.svg',
          imageAlt: 'United Team Building event overview'
        }
      ]
    },
    {
      id: 'time',
      title: 'TIME',
      questions: [
        {
          id: 'time-1',
          question: 'How long do team building events usually run?',
          answer: 'Most sessions run from 60 to 180 minutes, with flexible pacing available for shorter or full-day schedules.'
        },
        {
          id: 'time-2',
          question: 'How far in advance should we schedule?',
          answer: 'We recommend scheduling 2 to 4 weeks in advance, though we can often support faster turnarounds based on availability.'
        }
      ]
    },
    {
      id: 'options',
      title: 'OPTIONS',
      questions: [
        {
          id: 'options-1',
          question: 'Are remote and hybrid event formats available?',
          answer: 'Absolutely. We support fully remote and hybrid formats with facilitator guidance and clear participation instructions.'
        },
        {
          id: 'options-2',
          question: 'Can we combine multiple activities in one event?',
          answer: 'Yes. We can create a blended agenda with multiple modules to fit your event goals and team preferences.'
        }
      ]
    },
    {
      id: 'configuration',
      title: 'CONFIGURATION',
      questions: [
        {
          id: 'configuration-1',
          question: 'What information is needed to configure our event?',
          answer: 'We typically need participant count, preferred date/time, format, goals, and any technical or accessibility requirements.'
        },
        {
          id: 'configuration-2',
          question: 'Can event settings be updated after booking?',
          answer: 'Yes. We support updates to session structure, participation settings, and logistics prior to final confirmation.'
        }
      ]
    },
    {
      id: 'support',
      title: 'SUPPORT',
      questions: [
        {
          id: 'support-1',
          question: 'Is live support available during the event?',
          answer: 'Yes. We provide live facilitator and technical support so your team has help whenever needed.'
        },
        {
          id: 'support-2',
          question: 'What happens if attendees encounter access issues?',
          answer: 'Our team troubleshoots quickly and provides fallback instructions to keep participants engaged without interruption.'
        }
      ]
    },
    {
      id: 'metrics',
      title: 'METRICS',
      questions: [
        {
          id: 'metrics-1',
          question: 'Do you provide results or engagement metrics after events?',
          answer: 'Yes. We can provide participation and engagement summaries, along with key observations and suggested follow-up actions.'
        },
        {
          id: 'metrics-2',
          question: 'Can we track recurring team building outcomes over time?',
          answer: 'We can help establish repeatable reporting so you can compare trends and evaluate progress across multiple events.'
        }
      ]
    },
    {
      id: 'security',
      title: 'SECURITY',
      questions: [
        {
          id: 'security-1',
          question: 'How is participant data protected?',
          answer: 'We implement strict data protection measures, including encryption and access controls, to ensure participant information remains secure.'
        },
        {
          id: 'security-2',
          question: 'How is access managed?',
          answer: 'Each organization has 3 roles: Organization Administrator, Event Planner, Team Member. Only members of an organization can access their own organization\'s data. Organization Administrator will have full access to the organization\'s data and features.  Event Planner have access to features allowing them to manage events. Team Members only have access to their own audio and video content they upload to the event.'
        },
        {
          id: 'security-3',
          question: 'Are there any compliance certifications?',
          answer: 'Yes. Our platform complies with relevant industry standards and regulations to ensure data privacy and security.',
          imageSrc: 'assets/UTB-Logo.svg',
          imageAlt: 'United Team Building event overview'
        }
      ]
    }
  ];

  readonly expandedQuestions = new Set<string>();

  isExpanded(questionId: string): boolean {
    return this.expandedQuestions.has(questionId);
  }

  toggleQuestion(question: QaQuestion): void {
    if (this.expandedQuestions.has(question.id)) {
      this.expandedQuestions.delete(question.id);
      return;
    }

    this.expandedQuestions.add(question.id);
  }

  onQuestionItemClick(question: QaQuestion, event: MouseEvent): void {
    const target = event.target as HTMLElement | null;

    if (target?.closest('a, button')) {
      return;
    }

    this.toggleQuestion(question);
  }

  scrollToCategory(event: MouseEvent, categoryId: string): void {
    event.preventDefault();

    const target = document.getElementById(categoryId);
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  onAnswerLinkClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    const link = target?.closest('a');
    const href = link?.getAttribute('href');

    if (!href) {
      return;
    }

    if (/^(https?:|mailto:|tel:)/i.test(href)) {
      return;
    }

    const [rawPath, fragment] = href.split('#');

    if (!rawPath || !fragment) {
      return;
    }

    const routePath = rawPath.startsWith('/') ? rawPath : `/${rawPath}`;

    event.preventDefault();
    this.router.navigate([routePath], { fragment });
  }
}
